import React, { useState } from "react";
import { Check, Loader2, Minus } from "lucide-react";
import basiqMark from "../../assets/basiq-mark.png";
import { PLAN_META } from "../../lib/plans";
import { createCheckout } from "../../lib/api";
import { usePlan } from "../../context/PlanContext";
import { toast } from "sonner";

const ROWS = [
  { label: "Orçamentos por mês", free: "3", standard: "20", pro: "Ilimitados", bullets: { free: "3 orçamentos por mês", standard: "20 orçamentos por mês", pro: "Orçamentos ilimitados" } },
  { label: "Empresas / marcas", free: "1", standard: "3", pro: "Ilimitadas", bullets: { free: "1 empresa", standard: "3 empresas / marcas", pro: "Empresas ilimitadas" } },
  { label: "Modelos A4 premium", free: "1", standard: "5", pro: "Todos os 10", bullets: { free: "1 modelo A4", standard: "5 modelos A4 premium", pro: "Todos os 10 modelos A4" } },
  { label: "Personalização (fontes, capa, contracapa)", free: false, standard: true, pro: true },
  { label: "Orçamentos pelo WhatsApp", free: false, standard: true, pro: true, hot: true },
  { label: "Troca de empresa pelo WhatsApp", free: false, standard: true, pro: true },
  { label: "PDF sem a marca Basiq", free: false, standard: true, pro: true },
  { label: "Google Drive + envio por e-mail", free: false, standard: false, pro: true },
  { label: "Suporte prioritário", free: false, standard: false, pro: true },
];

const Cell = ({ v }) => {
  if (v === true) return <Check size={16} strokeWidth={3} className="text-emerald-600 mx-auto" />;
  if (v === false) return <Minus size={14} className="text-slate-300 mx-auto" />;
  return <span className="text-[13px] font-semibold text-slate-800">{v}</span>;
};

export default function PlansGrid({ compact = false }) {
  const { plan, trialEligible } = usePlan();
  const [annual, setAnnual] = useState(true);
  const [busy, setBusy] = useState("");

  const start = async (p) => {
    const meta = PLAN_META[p];
    const lookup = annual ? meta.yearly_lookup : meta.monthly_lookup;
    setBusy(p);
    try {
      const { checkout_url } = await createCheckout(lookup);
      window.location.href = checkout_url;
    } catch {
      setBusy("");
      toast.error("Não foi possível iniciar o checkout. Tente novamente.");
    }
  };

  return (
    <div data-testid="upgrade-plans">
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-1 bg-slate-100 rounded-full p-1" data-testid="upgrade-billing-toggle">
          <button onClick={() => setAnnual(false)} data-testid="upgrade-billing-monthly" className={`text-[13px] font-medium px-4 py-1.5 rounded-full transition-all ${!annual ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>Mensal</button>
          <button onClick={() => setAnnual(true)} data-testid="upgrade-billing-yearly" className={`text-[13px] font-medium px-4 py-1.5 rounded-full transition-all inline-flex items-center gap-2 ${annual ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`}>
            Anual <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">2 meses grátis</span>
          </button>
        </div>
      </div>

      <div className="mt-8 grid gap-4 md:grid-cols-3">
        {["free", "standard", "pro"].map((p) => {
          const meta = PLAN_META[p];
          const price = p === "free" ? "0" : annual ? meta.yearly : meta.monthly;
          const isCurrent = plan === p;
          const rec = p === "standard";
          return (
            <div key={p} data-testid={`upgrade-plan-${p}`} className={`relative rounded-3xl p-6 bg-white flex flex-col ring-1 transition-all ${rec ? "ring-2 ring-[#4A3BFA] shadow-[0_24px_60px_-30px_rgba(74,59,250,0.45)]" : "ring-slate-200"}`}>
              {rec && <span className="absolute -top-3 left-6 text-[10px] font-bold uppercase tracking-wider text-white bg-[#4A3BFA] rounded-full px-2.5 py-1">Mais popular</span>}
              {isCurrent && <span className="absolute -top-3 right-6 text-[10px] font-semibold text-slate-600 bg-slate-100 ring-1 ring-slate-200 rounded-full px-2.5 py-1">Seu plano</span>}
              <div className="flex items-center gap-2 text-[15px] font-bold text-slate-900">{p !== "free" && <img src={basiqMark} alt="" className="h-4 w-auto" />} Basiq {meta.name}</div>
              <div className="mt-3 leading-none">
                <span className="text-[34px] font-extrabold tracking-tight text-slate-900">R${price}</span>
                <span className="text-[13px] text-slate-400">/mês</span>
                {p !== "free" && annual && <div className="text-[11px] text-slate-400 mt-1">cobrado anualmente</div>}
              </div>
              <div className="mt-5 space-y-2.5 flex-1">
                {ROWS.filter((r) => r[p] !== false).slice(0, compact ? 4 : ROWS.length).map((r) => (
                  <div key={r.label} className="flex items-start gap-2 text-[13px] text-slate-600">
                    <Check size={14} strokeWidth={3} className={`shrink-0 mt-0.5 ${r.hot ? "text-[#25D366]" : "text-slate-900"}`} />
                    <span>{r.bullets ? <b className="text-slate-900 font-semibold">{r.bullets[p]}</b> : r.label}</span>
                  </div>
                ))}
              </div>
              {p === "free" ? (
                <div className="mt-6 h-11 grid place-items-center text-[13px] text-slate-400">{isCurrent ? "Você está aqui" : "Plano gratuito"}</div>
              ) : (
                <button onClick={() => start(p)} disabled={!!busy || isCurrent} data-testid={`upgrade-cta-${p}`}
                  className={`mt-6 h-11 rounded-full text-[14px] font-semibold inline-flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-60 ${rec ? "cta-upgrade" : "bg-slate-900 text-white hover:bg-slate-800"}`}>
                  {busy === p ? <Loader2 size={16} className="animate-spin" /> : isCurrent ? "Plano atual" : trialEligible === false ? "Assinar agora" : "Testar 7 dias grátis"}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {!compact && (
        <div className="mt-10 overflow-x-auto rounded-3xl ring-1 ring-slate-200 bg-white" data-testid="upgrade-compare-table">
          <table className="w-full min-w-[560px] text-left">
            <thead>
              <tr className="text-[11px] uppercase tracking-wider text-slate-400">
                <th className="px-5 py-4 font-semibold">Recurso</th>
                {["free", "standard", "pro"].map((p) => <th key={p} className="px-4 py-4 font-semibold text-center">{PLAN_META[p].name}</th>)}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r, i) => (
                <tr key={r.label} className={i % 2 ? "bg-slate-50/60" : ""}>
                  <td className="px-5 py-3 text-[13px] text-slate-700">{r.label}{r.hot && <span className="ml-2 text-[10px] font-bold uppercase tracking-wide text-[#128C7E] bg-[#25D366]/10 rounded-full px-2 py-0.5">Destaque</span>}</td>
                  <td className="px-4 py-3 text-center"><Cell v={r.free} /></td>
                  <td className="px-4 py-3 text-center"><Cell v={r.standard} /></td>
                  <td className="px-4 py-3 text-center"><Cell v={r.pro} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-5 text-center text-[12px] text-slate-400">{trialEligible === false ? "Cancele quando quiser, sem burocracia" : "7 dias grátis em qualquer plano pago · cancele quando quiser, sem burocracia"}</p>
    </div>
  );
}
